import {assign, createMachine, fromPromise} from 'xstate';

import {textSynthesis} from "./textSynthesis";
import {getTextSynthesisOptions, saveProgressToLocalStorage} from "./mainStateMachine.helpers";

import {machine as m} from "../constants/machine.constants";
import {localStorageKeys as k} from "../index";

const commonTransitions = {
    [m.events.IDLE]: {target: m.state.idle},
    [m.events.PAUSE]: {target: m.state.paused},
    [m.events.SET_LANGS]: {actions: "setLangs"},
    [m.events.NEXT]: [
        {target: m.state.paused, actions: m.actions.nextIndex, guard: "isIndexPagination"},
        {target: m.state.paused, actions: m.actions.nextCardIndex, guard: "isNestedPagination"},
    ],
    [m.events.PREV]: [
        {target: m.state.paused, actions: m.actions.prevIndex, guard: "isIndexPagination"},
        {target: m.state.paused, actions: m.actions.prevCardIndex, guard: "isNestedPagination"},
    ]
};

const mainStateMachine = createMachine(
    {
        id: 'wordMachine',
        initial: 'idle',
        context: {},

        states: {
            idle: {
                on: {
                    [m.events.SET_DATA]: {target: m.state.idle, actions: m.actions.setData},
                    [m.events.RUN]: {target: m.state.playingScenario},
                    ...commonTransitions,
                },
            },
            playingScenario: {
                entry: assign(() => ({isCardPagination: false})),
                always: [
                    // todo make transition if delay=0
                    {actions: m.actions.nextIndex, guard: "isCardComplete"},
                    {actions: m.actions.nextCardIndex, guard: "isScenarioComplete"},
                ],

                on: {
                    ...commonTransitions,

                    [m.events.NEXT_STEP]: {actions: m.actions.nextScenarioStep, target: m.state.playingScenario},
                    [m.events.SOUND]: {target: m.state.sound},
                    [m.events.PAUSE_TEXT]: [
                        {target: m.state.delayPlayingScenario, guard: "isDelayPlayingScenario"}
                    ],
                },
            },
            scenarioStep: {
                always: {target: m.state.playingScenario, actions: m.actions.nextScenarioStep}
            },
            sound: {
                always: [
                    {target: m.state.scenarioStep, guard: "isRepeatsComplete"},
                    {target: m.state.textSynthesis}

                ],
                on: {
                    ...commonTransitions,
                    [m.events.NEXT_REPEAT]: [
                        {target: m.state.playingScenario, guard: "isRepeatsComplete"},
                        {target: m.state.delaySound, guard: "isDelaySound", actions: m.actions.nextRepeat},
                        {target: m.state.sound, actions: m.actions.nextRepeat}
                    ],

                }
            },
            delayPlayingScenario: {
                after: {
                    DELAY: {target: m.state.playingScenario, actions: m.actions.nextScenarioStep}
                },
                on: {
                    [m.events.RUN]: {target: m.state.playingScenario},
                    ...commonTransitions
                },
            },
            delaySound: {
                after: {
                    DELAY_SOUND: {target: m.state.sound}
                },
                on: {
                    ...commonTransitions,
                    [m.events.RUN]: {target: m.state.playingScenario},
                    "TOGGLE_PAUSE_PLAY": {target: m.state.paused}
                },
            },
            paused: {
                entry: [assign(() => ({isCardPagination: true})), m.actions.saveToLocalStorage],
                always: {actions: () => window.speechSynthesis.cancel()},
                on: {
                    ...commonTransitions,
                    [m.events.RUN]: {target: m.state.refresh},
                    [m.events.INCREMENT]: {target: m.state.paused, actions: [m.actions.increment, m.actions.saveToLocalStorage]},
                    [m.events.DECREMENT]: {target: m.state.paused, actions: [m.actions.decrement, m.actions.saveToLocalStorage]},
                    // REFRESH: {target: m.state.sound, actions: () => console.log("REFRESH")},
                    [m.events.REFRESH]: {target: m.state.refresh},
                    "TOGGLE_PAUSE_PLAY": {target: m.state.sound}
                },
            },
            refresh: {
                always: {target: m.state.sound},
                // on: {
                //     REFRESH: {target: m.state.sound, actions: [m.actions.nextScenarioStep]},
                // }
            },
            textSynthesis: {
                entry: assign(() => ({isTextSynthesisPending: true})),
                invoke: {
                    id: "textSynthesis",
                    src: fromPromise(({input}) => textSynthesis({input})),

                    input: ({context, self}) => {
                        return {parent: self, ...getTextSynthesisOptions(context)}
                    },
                    onDone: {target: m.state.delaySound, actions: m.actions.nextRepeat},
                    onError: {
                        target: m.state.error,
                        actions: m.actions.handleError
                    }
                },
                on: {
                    ...commonTransitions,
                    "PENDING_END": {actions: assign(() => ({isTextSynthesisPending: false}))},
                }
            },
            error: {
                on: {
                    ...commonTransitions,
                    [m.events.RETRY]: {target: m.state.playingScenario, actions: assign(() => ({errorMessages: ""}))},
                    [m.events.RESET]: {target: m.state.idle},
                    HANDLE_ERROR: {
                        actions: assign(({context, event}) => {
                            console.log("HANDLE_ERROR");
                            return {errorMessages: event.error}
                        }),
                        target: m.state.paused
                    }
                }
            },
        },
    }, {
        actions: {
            setData: assign(({event}) => (event.context)),

            setLangs: assign(({event}) => {
                console.log(">>>",event.studyLang);

                return ({
                    [m.contextFields.langs.study]: event.studyLang,
                    [m.contextFields.langs.base]: event.baseLang
                })

            }),

            setSound: assign(({context}) => {
                    // const currentIndex = context[m.contextFields.scenario.currentIndex];
                    // const {repeats, delay} = context.scenario[currentIndex].props
                    // return {soundDelay: delay, leftSoundRepeats: repeats}
                    return {}
                }
            ),

            nextScenarioStep: assign(({context}) => {
                const {[m.contextFields.scenario.currentIndex]: currentIndexScenario, scenario} = context;
                const dataLength = scenario.length;
                const newScenarioIndex = (currentIndexScenario + 1) % dataLength
                const newScenarioItem = scenario[newScenarioIndex];
                const {repeats: newRepeats, delay: newDelay} = newScenarioItem.props || {}

                return {
                    [m.contextFields.scenario.currentIndex]: newScenarioIndex,
                    [m.contextFields.scenario.currentItem]: newScenarioItem,
                    [m.contextFields.isScenarioCompleted]: dataLength - 1 === currentIndexScenario,
                    [m.contextFields.step.delay]: newDelay !== undefined ? newDelay : 1,
                    [m.contextFields.sound.leftRepeats]: newRepeats !== undefined ? newRepeats : 0
                }
            }),

            nextRepeat: assign(({context}) => {
                const {leftRepeats} = context;
                return {leftRepeats: leftRepeats - 1}
            }),

            nextCardIndex: assign(({context}) => {
                const {currentIndex, currentCardIndex, currentScenarioIndex, data} = context
                if (data.length === 0) return {}

                const dataLength = data[currentIndex].scenarioItems?.length;
                const isCardComplete = dataLength - 1 === currentCardIndex;
                const newCardIndex = isCardComplete ? currentCardIndex : (currentCardIndex + 1) % dataLength;
                const newScenarioIndex = isCardComplete ? currentScenarioIndex : 0;
                return {
                    [m.contextFields.data.currentCardIndex]: newCardIndex,
                    [m.contextFields.scenario.currentIndex]: newScenarioIndex,
                    [m.contextFields.isScenarioCompleted]: false,
                    [m.contextFields.isCardsCompleted]: isCardComplete
                }
            }),
            prevCardIndex: assign(({context}) => {
                const {currentIndex, currentCardIndex, currentScenarioIndex, data} = context;
                if (data.length === 0) return {}
                const dataLength = data[currentIndex].scenarioItems.length;
                const isCardComplete = currentCardIndex === 0;
                const newCardIndex = isCardComplete ? currentCardIndex : (currentCardIndex - 1) % dataLength;
                const newScenarioIndex = isCardComplete ? currentScenarioIndex : 0;
                return {
                    [m.contextFields.data.currentCardIndex]: newCardIndex,
                    [m.contextFields.scenario.currentIndex]: newScenarioIndex,
                    [m.contextFields.isScenarioCompleted]: false,
                    [m.contextFields.isCardsCompleted]: isCardComplete
                }
            }),
            nextIndex: assign(({context}) => {
                const {currentIndex, data} = context;
                const dataLength = data.length;
                const newIndex = (currentIndex + 1) % dataLength;
                return {
                    [m.contextFields.data.currentIndex]: newIndex,
                    [m.contextFields.data.currentCardIndex]: 0,
                    [m.contextFields.isCardsCompleted]: false,
                    [m.contextFields.isCardPagination]: false
                }
            }),
            prevIndex: assign(({context}) => {
                const {currentIndex, data} = context;
                const dataLength = data.length;
                const newIndex = (currentIndex - 1 + data.length) % dataLength;
                return {
                    [m.contextFields.data.currentIndex]: newIndex,
                    [m.contextFields.data.currentCardIndex]: 0,
                    [m.contextFields.isCardsCompleted]: false,
                    [m.contextFields.isCardPagination]: false
                }
            }),

            increment: assign(({context, event}) => {
                const {payload, guard} = event.data;
                const delta = event.data.delta || 1;
                const newValue = Math.min((payload.value + 1) * delta, 9 * delta);
                let result = {[payload.name]: newValue};

                if (!guard) {
                    return result;
                } else {
                    const currentItem = context[m.contextFields.scenario.currentItem];
                    const oldValue = currentItem.props[payload.name] || 0;
                    const diff = newValue - oldValue;

                    // Reactive update of session counters
                    let sessionUpdate = {};
                    if (payload.name === "repeats") {
                        sessionUpdate.leftRepeats = Math.max(0, (context.leftRepeats || 0) + diff);
                    } else if (payload.name === "delay") {
                        sessionUpdate.stepDelay = Math.max(0, (context.stepDelay || 0) + diff);
                    }

                    const newCurrentItem = {...currentItem, props: {...currentItem.props, ...result}};
                    const scenario = [...context[m.contextFields.scenario.currentNode]];
                    const currentIndexScenario = context[m.contextFields.scenario.currentIndex];
                    scenario.splice(currentIndexScenario, 1, newCurrentItem);

                    return {
                        [m.contextFields.scenario.currentItem]: newCurrentItem,
                        [m.contextFields.scenario.currentNode]: scenario,
                        ...sessionUpdate
                    };
                }
            }),
            decrement: assign(({context, event}) => {
                const {payload, guard} = event.data;
                const delta = event.data.delta || 1;
                const newValue = Math.max((payload.value - 1) * delta, 0);
                let result = {[payload.name]: newValue};

                if (!guard) {
                    return result;
                } else {
                    const currentItem = context[m.contextFields.scenario.currentItem];
                    const oldValue = currentItem.props[payload.name] || 0;
                    const diff = newValue - oldValue;

                    // Reactive update of session counters
                    let sessionUpdate = {};
                    if (payload.name === "repeats") {
                        sessionUpdate.leftRepeats = Math.max(0, (context.leftRepeats || 0) + diff);
                    } else if (payload.name === "delay") {
                        sessionUpdate.stepDelay = Math.max(0, (context.stepDelay || 0) + diff);
                    }

                    const newCurrentItem = {...currentItem, props: {...currentItem.props, ...result}};
                    const scenario = [...context[m.contextFields.scenario.currentNode]];
                    const currentIndexScenario = context[m.contextFields.scenario.currentIndex];
                    scenario.splice(currentIndexScenario, 1, newCurrentItem);

                    return {
                        [m.contextFields.scenario.currentItem]: newCurrentItem,
                        [m.contextFields.scenario.currentNode]: scenario,
                        ...sessionUpdate
                    };
                }
            }),
            [m.actions.saveToLocalStorage]: ({context}) => {
                saveProgressToLocalStorage(context, k.appContext);
            },
            handleError: ({context, event}) => {
                context.errorMessages = event.error.message
            },
        },
        guards: {
            isScenarioComplete: ({context}) => {
                return context.isScenarioCompleted;
            },
            isCardComplete: ({context}) => {
                return context.isCardsCompleted;
            },
            isNestedPagination: ({context}) => {
                const {isCardPagination, isCardsCompleted} = context;
                return isCardPagination || !isCardsCompleted
            },
            isIndexPagination: ({context}) => {
                const {isCardPagination, isCardsCompleted} = context
                return !isCardPagination || isCardsCompleted
            },

            isRepeatsComplete: ({context}) => {
                return context.leftRepeats === 0
            },
            isDelaySound: ({context}) => context.stepDelay > 0,
            isDelayPlayingScenario: ({context}) => context.stepDelay > 0
        },
        delays: {
            DELAY: ({context}) => context.stepDelay * 1000,
            DELAY_SOUND: ({context}) => context.stepDelay * 1000,
        }
    })
;


export {mainStateMachine}

